import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ApiError } from 'requests/RequestBase';
import { FormErrors } from 'validators/LoginValidator';
import { LoginParameters } from 'components/templates/RedirectLoginTemplate';
import { ActionBase } from 'store/ActionBase';
import { Store } from 'store/Store';
import {
	LoginResData,
	CommonLoginV2ResData,
	GetLoginDisplayItemResData,
	TermsRedirectResData,
	callRedirectLoginApi,
} from 'requests/RequestLogin';
import { RoutePath } from 'consts/RoutePath';
import {
	convertObjectToGetParameter,
	convertObjectToGetParameterWithEncode,
} from 'common/ObjectToGetParameterConverter';
import { HttpStatus } from 'consts/HttpStatus';
import { findErrorMessageByApiError } from 'consts/ApiErrorCode';
import { ReactError } from 'consts/ReactError';

/**
 * 共通ログイン処理のカスタムフック
 * フォームバリデーション -> ログイン実処理 -> ページ遷移 を段階的に分けている
 * useStateが非同期なのでuseEffectを使って段階的にしないとエラーをキャッチすることができないため
 */
export const useRedirectLogin = (
	formErrors: FormErrors,
	setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
	getErrorMessage: Function,
	loginParameters: LoginParameters,
	store: Store,
	setProcessLoading: React.Dispatch<React.SetStateAction<boolean>>,
) => {
	const history = useHistory();
	// フォームバリデーション
	const [isFormValidationFinished, setIsFormValidationFinished] = useState(false);
	const login = async () => {
		setProcessLoading(true);

		Object.keys(formErrors).forEach((k) => {
			setFormErrors((prev) => ({ ...prev, [k]: getErrorMessage(k, loginParameters[k]) }));
		});
		setIsFormValidationFinished(true);
	};

	// ログイン実処理
	const [isLoginFinished, setIsLoginFinished] = useState(false);
	type ResponseData = {
		loginResData: LoginResData;
	};
	const [resDatas, setResDatas] = useState<ResponseData>({ loginResData: undefined });
	useEffect(() => {
		if (!isFormValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '');
		if (isError) {
			setIsFormValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		const executeLogin = async () => {
			const result = await callRedirectLoginApi({
				gid: loginParameters.gakkenId,
				pw: loginParameters.password,
				...store.redirectLoginData,
			});
			if (result.status === HttpStatus.INTERNAL_SERVER_ERROR) {
				setIsLoginFinished(true);
				return;
			}
			if (result.status === HttpStatus.GATEWAY_TIMEOUT) {
				setIsLoginFinished(true);
				return;
			}
			if (result.status === HttpStatus.REDIRECT) {
				const termsData = result.data as TermsRedirectResData;
				const getParameters = convertObjectToGetParameterWithEncode(
					store.authorizeV2Data,
					'code_challenge',
					'redirect_uri',
					'nonce',
				);
				if (termsData.type === 'terms') {
					// パラメータはURLエンコードされている状態
					history.push(
						`${RoutePath.TERMS}?from=regchild&isFirstAgreement=${termsData.isFirstAgreement}&${getParameters}`,
					);
				}
				return;
			}
			if (result.status !== HttpStatus.OK) {
				const errorData = result.data as ApiError;
				setIsLoginFinished(true);
				return;
			}
			const data = result.data as LoginResData;
			setResDatas((prev) => ({ ...prev, loginResData: data }));
			setIsLoginFinished(true);
		};
		executeLogin();
	}, [isFormValidationFinished]);

	// ページ遷移
	useEffect(() => {
		if (!isLoginFinished) {
			return;
		}

		// リダイレクト先urlがエンコードされたままなのでデコードしてからリダイレクトする
		const redirectUrl = `${RoutePath.MEMBER_ADD}`;
		// const redirectUrl = `${decodeURIComponent(store.authorizeV2Data.redirect_uri)}`;
		history.push(redirectUrl);
	}, [isLoginFinished]);

	return {
		login,
	};
};
